import * as React from "react";
import {
  List,
  Datagrid,
  FunctionField,
  Show,
  TabbedShowLayout,
  Edit,
  SimpleForm,
  TextInput,
  SelectInput,
  BooleanInput,
  required,
  email,
  TopToolbar,
  ExportButton,
  FilterButton,
  useRecordContext,
  EditButton,
  TabbedShowLayoutTabs,
} from "react-admin";
import {
  Typography as MuiTypography,
  Box,
  Card,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { EnumChip } from "../../components/EnumChip";
import { ConfirmActionButton } from "../../components/ConfirmActionButton";
import { RemoteList, RemoteObject } from "../../components/RemoteList";
import { ReferralPanel } from "../../components/ReferralPanel";
import { DocumentDownloadButton } from "../../components/DocumentDownloadButton";
import { InfoGrid } from "../../components/InfoGrid";
import { FrDateField } from "../../components/FrDateField";
import { ACCOUNT_STATUS, GENDER, BOOKING_TYPE, SUGGESTION_STATUS, choicesFromEnum, labelOf } from "../../enums";
import { PersonChip, Section, CardGrid, TechInfo, StatusChip } from "../../components/ui";
import { ProfileHeader, RecordLink, bookingColumns, forfaitColumns, paymentStatusChip, Stars } from "../../components/profile";
import { fmtDate, fmtDateTime, fmtMoney, fmtCents, fullName } from "../../utils/format";
import { httpClient } from "../../api/httpClient";
import { API_BASE_URL } from "../../api/config";

// pick : retourne la 1ère valeur non nulle parmi plusieurs clés possibles
// (les DTO backend varient : startAt vs startTime vs slot.startTime…).
const pick = (row, ...keys) => {
  for (const k of keys) {
    const parts = k.split(".");
    let v = row;
    for (const p of parts) v = v == null ? undefined : v[p];
    if (v != null && v !== "") return v;
  }
  return undefined;
};

// Filtres supportés par GET /users?type=PROFESSIONAL : specialty (nom de
// spécialité ou métier), city, consultationType (VISIO | OFFICE).
// Recherche texte / statut / validé : non supportés côté backend → retirés.
const proFilters = [
  <TextInput key="specialty" source="specialty" label="Spécialité / métier" alwaysOn />,
  <TextInput key="city" source="city" label="Ville" />,
  <SelectInput key="consultationType" source="consultationType" label="Mode" choices={[
    { id: "VISIO", name: "Visio" },
    { id: "OFFICE", name: "Cabinet" },
  ]} />,
  // Comptes de tests internes (@iouman.com) — masqués par défaut via
  // resourceMap.listFilter.hideTestUsers=true. Décocher réaffiche les comptes de tests.
  <BooleanInput
    key="hideTestUsers"
    source="hideTestUsers"
    label="Masquer les comptes de test"
    defaultValue={true}
  />,
  <BooleanInput key="showDeleted" source="showDeleted" label="Afficher les comptes supprimés" />,
];

// Téléphone du pro (profil : phoneNumber ; repli sur l'adresse du cabinet),
// avec un lien WhatsApp pour l'inscrire au groupe d'accompagnement.
const phoneOf = (r) => pick(r || {}, "phoneNumber", "phone", "address.phone");
const toWhatsAppNumber = (raw) => {
  let d = String(raw).replace(/[^\d+]/g, "");
  if (d.startsWith("+")) return d.slice(1);
  if (d.startsWith("00")) return d.slice(2);
  if (d.startsWith("0")) return "33" + d.slice(1);
  return d;
};
const PhoneCell = ({ record }) => {
  const phone = phoneOf(record);
  if (!phone) return <span>—</span>;
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" onClick={(e) => e.stopPropagation()} sx={{ whiteSpace: "nowrap" }}>
      <Box component="a" href={`tel:${phone}`} sx={{ color: "text.primary", textDecoration: "none", fontSize: 14 }}>{phone}</Box>
      <Tooltip title="Ouvrir dans WhatsApp">
        <IconButton
          size="small"
          component="a"
          href={`https://wa.me/${toWhatsAppNumber(phone)}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="WhatsApp"
          sx={{ color: "#25D366", p: 0.5 }}
        >
          <WhatsAppIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
};

// Pas de création ici (aucun POST /users) : « Inviter un pro / patient ».
const ListActions = () => (
  <TopToolbar>
    <FilterButton />
    <ExportButton />
  </TopToolbar>
);

// Validé = compte ACTIVE (PUT /pro/{id}/validate passe le compte en ACTIVE).
const isValidated = (r) => r?.accountStatus === "ACTIVE";
const cityOf = (r) =>
  pick(r || {}, "city", "address.city", "professionalAddresses.0.city", "professionalCard.professionalAddresses.0.city");
const isFeatured = (r) => Boolean(r?.featured ?? r?.isFeatured);

export const ProList = () => (
  <List
    filters={proFilters}
    actions={<ListActions />}
    filterDefaultValues={{ hideTestUsers: true }}
    perPage={25}
    sort={{ field: "creationDate", order: "DESC" }}
  >
    <Datagrid rowClick="show" bulkActionButtons={false}>
      <FunctionField
        label="Professionnel"
        sortBy="lastName"
        render={(r) => <PersonChip firstName={r.firstName} lastName={r.lastName} email={r.email} photo={r?.photo?.url} resource="professionals" />}
      />
      <FunctionField
        label="Spécialités"
        render={(r) => {
          const specs = (r?.specialityNames?.length ? r.specialityNames : (r?.professionalCard?.specialities || []).map((sp) => sp?.speciality?.name)).filter(Boolean);
          return specs.length ? (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {specs.slice(0, 2).map((n) => <Chip key={n} size="small" label={n} variant="outlined" />)}
              {specs.length > 2 && <Chip size="small" label={`+${specs.length - 2}`} />}
            </Stack>
          ) : (
            r?.job || "—"
          );
        }}
      />
      <FunctionField label="Téléphone" render={(r) => <PhoneCell record={r} />} />
      <FunctionField label="Ville" render={(r) => cityOf(r) || "—"} />
      <FunctionField
        label="Validation"
        render={(r) => (isValidated(r) ? <Chip size="small" color="success" label="Validé" /> : <Chip size="small" color="warning" label="À valider" />)}
      />
      <EnumChip source="accountStatus" enumObj={ACCOUNT_STATUS} label="Statut" />
      <FrDateField source="creationDate" label="Inscrit le" />
    </Datagrid>
  </List>
);

const ValidateButton = () => {
  const r = useRecordContext();
  if (!r || isValidated(r)) return null;
  return (
    <ConfirmActionButton
      label="Valider"
      color="success"
      confirmTitle="Valider ce professionnel"
      confirmText={`Valider ${r.firstName} ${r.lastName} ?`}
      successMessage="Pro validé"
      onConfirm={() =>
        httpClient(`${API_BASE_URL}/pro/${r.id}/validate`, { method: "PUT" })
      }
    />
  );
};

const SuspendButton = () => {
  const r = useRecordContext();
  if (!r) return null;
  return (
    <ConfirmActionButton
      label="Suspendre"
      color="warning"
      confirmTitle="Suspendre ce professionnel"
      confirmText={`Suspendre ${r.firstName} ${r.lastName} ?`}
      successMessage="Pro suspendu"
      onConfirm={() =>
        httpClient(`${API_BASE_URL}/users/status/${r.id}/false`, { method: "PUT" })
      }
    />
  );
};

const FeaturedButton = () => {
  const r = useRecordContext();
  if (!r) return null;
  return (
    <ConfirmActionButton
      label={isFeatured(r) ? "Retirer de la mise en avant" : "Mettre en avant"}
      color={isFeatured(r) ? "warning" : "success"}
      confirmTitle="Mise en avant"
      confirmText={isFeatured(r)
        ? `Retirer ${r.firstName || ""} ${r.lastName || ""} de la mise en avant ?`
        : `Mettre en avant ${r.firstName || ""} ${r.lastName || ""} ?`}
      successMessage="Mise en avant mise à jour"
      onConfirm={() =>
        // PUT /pro/{id}/featured?isFeatured=true|false (query param, pas de body)
        httpClient(`${API_BASE_URL}/pro/${r.id}/featured?isFeatured=${!isFeatured(r)}`, {
          method: "PUT",
        })
      }
    />
  );
};

const DeleteProButton = () => {
  const r = useRecordContext();
  if (!r) return null;
  return (
    <ConfirmActionButton
      label="Supprimer le compte"
      color="error"
      confirmTitle="Supprimer DÉFINITIVEMENT ce pro"
      confirmText={`Cette action est irréversible. Confirmer pour ${r.firstName} ${r.lastName} ?`}
      successMessage="Pro supprimé"
      onConfirm={() => httpClient(`${API_BASE_URL}/pro/${r.id}`, { method: "DELETE" })}
    />
  );
};

const T = TabbedShowLayout.Tab;

// Spécialités & tarifs : ProfessionalCardDTO.specialities[] (SpecialityPriceDTO)
// + grille de tarifs (pricings[]).
const SpecialitiesPanel = () => {
  const r = useRecordContext();
  if (!r) return null;
  const specs = r?.professionalCard?.specialities || [];
  const pricings = r?.professionalCard?.pricings || [];
  const eur = (v) => fmtMoney(v);
  return (
    <Stack spacing={3}>
      <Section title="Spécialités et prix des séances">
        {specs.length === 0 ? (
          <MuiTypography variant="body2" color="text.secondary">Aucune spécialité renseignée</MuiTypography>
        ) : (
          <Box sx={{ overflowX: "auto", border: 1, borderColor: "divider", borderRadius: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Spécialité</TableCell>
                  <TableCell>Domaine</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell align="right">Prix patient</TableCell>
                  <TableCell align="right">Part pro</TableCell>
                  <TableCell align="right">Frais plateforme</TableCell>
                  <TableCell align="right">Net pro</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {specs.map((sp, i) => (
                  <TableRow key={sp?.speciality?.id || i} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: sp?.speciality?.color || sp?.color || "primary.main" }} />
                        <MuiTypography variant="body2" sx={{ fontWeight: 600 }}>{sp?.speciality?.name || "—"}</MuiTypography>
                      </Stack>
                    </TableCell>
                    <TableCell>{sp?.speciality?.domainMedical || "—"}</TableCell>
                    <TableCell><StatusChip value={sp?.speciality?.status} enumObj={SUGGESTION_STATUS} /></TableCell>
                    <TableCell align="right">{eur(sp?.patientPrice ?? sp?.price)}</TableCell>
                    <TableCell align="right">{eur(sp?.proPrice)}</TableCell>
                    <TableCell align="right">{eur(sp?.platformFee)}</TableCell>
                    <TableCell align="right">{eur(sp?.proNet)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Section>
      {pricings.length > 0 && (
        <Section title="Grille tarifaire">
          <Box sx={{ overflowX: "auto", border: 1, borderColor: "divider", borderRadius: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Prestation</TableCell>
                  <TableCell align="right">Prix</TableCell>
                  <TableCell align="right">Part pro</TableCell>
                  <TableCell align="right">Part ioUman</TableCell>
                  <TableCell align="right">Net pro</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pricings.map((p, i) => (
                  <TableRow key={i} hover>
                    <TableCell>{p?.name || "—"}</TableCell>
                    <TableCell align="right">{eur(p?.price)}</TableCell>
                    <TableCell align="right">{eur(p?.proPrice)}</TableCell>
                    <TableCell align="right">{eur(p?.ioumanPrice)}</TableCell>
                    <TableCell align="right">{eur(p?.proNet)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Section>
      )}
      <Section title="Métier & offre">
        <InfoGrid
          fields={[
            { label: "Métier", source: "job" },
            { label: "Métiers additionnels", source: "additionalJobs", type: "list" },
            { label: "Abonnement", source: "subscriptionEnum" },
            { label: "Préfère la visio", source: "prefersVisio", type: "bool" },
            { label: "Préfère le cabinet", source: "prefersOffice", type: "bool" },
            { label: "Offre visio", source: "professionalCard.offerVisio", type: "bool" },
            { label: "Offre cabinet", source: "professionalCard.offerPresentiel", type: "bool" },
            { label: "Accepte de nouveaux patients", source: "professionalCard.allowNewPatient", type: "bool" },
            { label: "Réservable", source: "bookable", type: "bool" },
            { label: "Types de consultation", source: "professionalCard.preferredConsultationTypes", type: "list" },
            { label: "Modes de paiement", source: "professionalCard.paymentModeEnum", type: "list" },
          ]}
        />
      </Section>
    </Stack>
  );
};

export const ProShow = () => (
  <Show component="div" actions={false}>
    <ProfileHeader
      kind="pro"
      chips={<ProHeaderChips />}
      actions={
        <>
          <ValidateButton />
          <FeaturedButton />
          <EditButton variant="outlined" label="Modifier" />
        </>
      }
    />
    <Card>
      <TabbedShowLayout
        syncWithLocation
        tabs={<TabbedShowLayoutTabs variant="scrollable" scrollButtons="auto" />}
        sx={{ "& .RaTabbedShowLayout-content": { p: { xs: 2, md: 3 } } }}
      >
        <T label="Profil">
          <CardGrid min={400}>
            <Section title="Identité & contact">
              <InfoGrid
                fields={[
                  { label: "Prénom", source: "firstName" },
                  { label: "Nom", source: "lastName" },
                  { label: "Nom marital", source: "lastNameMarried" },
                  { label: "Genre", source: (r) => (r?.gender ? labelOf(GENDER, r.gender) : null) },
                  { label: "Date de naissance", source: "birthdate", type: "date" },
                  { label: "Ville de naissance", source: "birthCity" },
                  { label: "Email", source: "email" },
                  { label: "Téléphone", source: "phoneNumber" },
                ]}
              />
            </Section>
            <Section title="Adresse du profil">
              <InfoGrid
                fields={[
                  { label: "Libellé", source: "address.adresseName" },
                  { label: "Rue", source: "address.street" },
                  { label: "Code postal", source: "address.postalCode" },
                  { label: "Ville", source: "address.city" },
                  { label: "Pays", source: "address.country" },
                  { label: "Téléphone cabinet", source: "address.phone" },
                  { label: "Étage", source: "address.floor" },
                  { label: "Ascenseur", source: "address.elevator", type: "bool" },
                  { label: "Parking", source: "address.parking", type: "bool" },
                  { label: "Accessible PMR", source: "address.accessible", type: "bool" },
                  { label: "Infos utiles", source: "address.usefulInformations" },
                  { label: "Coordonnées GPS", source: (r) => (r?.address?.latitude != null ? `${r.address.latitude}, ${r.address.longitude}` : null) },
                ]}
              />
            </Section>
            <Section title="Compte">
              <InfoGrid
                fields={[
                  { label: "Inscrit le", source: "creationDate", type: "datetime" },
                  { label: "Dernière mise à jour", source: "updateDate", type: "datetime" },
                  { label: "Validé", source: (r) => isValidated(r), type: "bool" },
                  { label: "Mis en avant", source: (r) => isFeatured(r), type: "bool" },
                  { label: "Email vérifié", source: "emailVerified", type: "bool" },
                  { label: "Vérifié", source: "isVerified", type: "bool" },
                  { label: "Actif", source: "active", type: "bool" },
                  { label: "Activé", source: "enabled", type: "bool" },
                  { label: "Profil complété", source: "profileFilled", type: "bool" },
                  { label: "Premier login", source: "firstLogin", type: "bool" },
                  { label: "Connexion via", source: "providerEnum" },
                  { label: "Type social", source: "socialType" },
                  { label: "Invitation envoyée", source: "invitationSent", type: "bool" },
                  { label: "Invitation envoyée le", source: "invitationSentDate", type: "datetime" },
                  { label: "Stripe Connect", source: "stripeOnboardingStatus" },
                ]}
              />
            </Section>
            <Section title="Présentation">
              <InfoGrid
                cols={1}
                fields={[
                  { label: "Présentation", source: "professionalCard.presentation" },
                  { label: "Langues parlées", source: (r) => r?.professionalCard?.languages?.map((l) => l?.name || l).filter(Boolean), type: "list" },
                  { label: "Diplômes", source: "professionalCard.diplomes", type: "list" },
                  { label: "Traits", source: (r) => r?.professionalCard?.traits?.map((t) => t?.name || t), type: "list" },
                  { label: "Loisirs", source: (r) => r?.professionalCard?.hobbies?.map((h) => h?.name || h), type: "list" },
                ]}
              />
            </Section>
            <Section title="Activité">
              <InfoGrid
                fields={[
                  { label: "Programmes", source: "programCount" },
                  { label: "Séances", source: "sessionCount" },
                  { label: "Minutes totales", source: "totalMinutes" },
                  { label: "Minutes forfait restantes", source: "forfaitRemainingMinutes" },
                ]}
              />
            </Section>
            <Section title="Codes & notes">
              <InfoGrid
                cols={1}
                fields={[
                  { label: "Code parrainage", source: "referralCode", type: "mono" },
                  { label: "Code du parrain", source: "referrerCode", type: "mono" },
                  { label: "Code affiliation", source: "affiliationCode", type: "mono" },
                  { label: "Notes admin", source: "notes" },
                ]}
              />
            </Section>
          </CardGrid>
          <Section title="Adresses des cabinets (fiche professionnelle)" sx={{ mt: 2, height: "auto" }}>
            <CabinetAddresses />
          </Section>
          <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
            <SuspendButton />
            <DeleteProButton />
          </Stack>
          <TechInfo />
        </T>

        <T label="Spécialités & tarifs" path="specialites">
          <SpecialitiesPanel />
        </T>

        <T label="Rendez-vous" path="rendez-vous">
          <Stack spacing={3}>
            <RemoteList buildPath={(r) => `/bookings/${r.id}/PROFESSIONAL`} emptyText="Aucun rendez-vous" columns={bookingColumns("patient")} />
            <Section title="Créneaux disponibles (2 mois)">
              <RemoteList
                buildPath={(r) => `/calendars/users/${r.id}/available-slots?months=2`}
                emptyText="Aucun créneau disponible"
                columns={[
                  { key: "start", label: "Début", render: (sl) => fmtDateTime(pick(sl, "startTime", "startAt", "start")) },
                  { key: "end", label: "Fin", render: (sl) => fmtDateTime(pick(sl, "endTime", "endAt", "end")) },
                  { key: "type", label: "Mode", render: (sl) => <StatusChip value={pick(sl, "type", "bookingType")} enumObj={BOOKING_TYPE} /> },
                  { key: "status", label: "Statut", render: (sl) => pick(sl, "status") || "—" },
                ]}
              />
            </Section>
            <Section title="Patients suivis">
              <RemoteList
                buildPath={(r) => `/users/${r.id}/patients`}
                emptyText="Aucun patient"
                columns={[
                  { key: "name", label: "Patient", render: (p) => (p?.id ? <RecordLink to={`/patients/${encodeURIComponent(p.id)}/show`}>{fullName(p.firstName, p.lastName, "Voir la fiche")}</RecordLink> : fullName(p.firstName, p.lastName, "—")) },
                  { key: "email", label: "Email", render: (p) => pick(p, "email") || "—" },
                  { key: "phone", label: "Téléphone", render: (p) => pick(p, "phone", "phoneNumber") || "—" },
                ]}
              />
            </Section>
          </Stack>
        </T>

        <T label="Paiements & factures" path="paiements">
          <Stack spacing={3}>
            <Section title="Paiements">
              <RemoteList
                buildPath={(r) => `/payment?proId=${r.id}&size=100`}
                emptyText="Aucun paiement"
                columns={[
                  { key: "date", label: "Date", render: (p) => fmtDateTime(pick(p, "createdAt", "paidAt")) },
                  { key: "desc", label: "Description", render: (p) => pick(p, "description") || "—" },
                  { key: "amount", label: "Montant", align: "right", render: (p) => fmtCents(pick(p, "amount")) },
                  { key: "status", label: "Statut", render: (p) => paymentStatusChip(pick(p, "paymentStatusEnum", "status")) },
                  { key: "booking", label: "", align: "right", render: (p) => (p?.bookingId ? <RecordLink to={`/bookings/${encodeURIComponent(p.bookingId)}/show`}>Voir le RDV →</RecordLink> : null) },
                ]}
              />
            </Section>
            <Section title="Virements reçus">
              <RemoteList
                buildPath={(r) => `/pro/${r.id}/transfers`}
                emptyText="Aucun virement"
                columns={[
                  { key: "date", label: "Date", render: (t) => fmtDateTime(pick(t, "date", "created", "createdAt")) },
                  { key: "amount", label: "Montant", align: "right", render: (t) => fmtMoney(pick(t, "amount")) },
                  { key: "booking", label: "", align: "right", render: (t) => (t?.bookingId ? <RecordLink to={`/bookings/${encodeURIComponent(t.bookingId)}/show`}>Voir le RDV →</RecordLink> : null) },
                ]}
              />
            </Section>
            <Section title="Forfaits">
              <RemoteList buildPath={(r) => `/forfaits/professional/${r.id}`} emptyText="Aucun forfait" columns={forfaitColumns} />
            </Section>
            <Section title="Factures émises">
              <RemoteList
                buildPath={(r) => `/documents/pro/${r.id}/factures`}
                emptyText="Aucune facture"
                columns={[
                  { key: "name", label: "Facture", render: (d) => pick(d, "name", "fileName") || "—" },
                  { key: "date", label: "Émise le", render: (d) => fmtDate(pick(d, "createdDate", "createdAt", "bookingDate")) },
                  { key: "patient", label: "Patient", render: (d) => fullName(pick(d, "patientFirstName"), pick(d, "patientLastName"), "—") },
                  { key: "dl", label: "", align: "right", render: (d) => <DocumentDownloadButton documentId={pick(d, "id")} label="Télécharger" /> },
                ]}
              />
            </Section>
            <CardGrid min={400}>
              <Section title="Coordonnées bancaires (RIB)">
                <RemoteList
                  buildPath={(r) => `/pro/${r.id}/bank-informations`}
                  emptyText="Aucun RIB enregistré"
                  columns={[
                    { key: "label", label: "Libellé", render: (b) => pick(b, "accountLabel") || "—" },
                    { key: "iban", label: "IBAN", render: (b) => pick(b, "iban") || "—" },
                    { key: "bic", label: "BIC", render: (b) => pick(b, "bic") || "—" },
                    { key: "bank", label: "Banque", render: (b) => pick(b, "bankName") || "—" },
                  ]}
                />
              </Section>
              <Section title="Stripe Connect">
                <RemoteObject
                  buildPath={(r) => `/pro/${r.id}/stripe-connect/status`}
                  emptyText="Aucune info Stripe Connect"
                  render={(st) => {
                    const ok = st?.hasStripeAccount;
                    const status = st?.status || "NONE";
                    const isActive = status === "ACTIVE" || status === "ENABLED";
                    return (
                      <Stack spacing={1.5}>
                        <Chip
                          sx={{ alignSelf: "flex-start" }}
                          color={isActive ? "success" : ok ? "warning" : "error"}
                          label={isActive ? "Compte actif" : ok ? "Compte créé — action requise" : "Pas de compte Stripe"}
                        />
                        <MuiTypography variant="body2" color="text.secondary">Statut d'onboarding : {status}</MuiTypography>
                        {st?.stripeAccountId && (
                          <TechInfo items={[{ key: "stripeAccountId", label: "Compte Stripe", value: st.stripeAccountId }]} showRaw={false} title="Identifiant Stripe" sx={{ mt: 0 }} />
                        )}
                      </Stack>
                    );
                  }}
                />
              </Section>
            </CardGrid>
          </Stack>
        </T>

        <T label="Documents" path="documents">
          <Stack spacing={3}>
            <RemoteList
              buildPath={(r) => `/documents/user/${r.id}?userType=PROFESSIONAL`}
              emptyText="Aucun document"
              columns={[
                { key: "name", label: "Fichier", render: (d) => pick(d, "name", "fileName") || "—" },
                { key: "type", label: "Type", render: (d) => pick(d, "documentTypeEnum", "documentType", "type") || "—" },
                { key: "date", label: "Ajouté le", render: (d) => fmtDate(pick(d, "createdDate", "uploadedAt", "createdAt")) },
                { key: "dl", label: "", align: "right", render: (d) => <DocumentDownloadButton documentId={pick(d, "id")} /> },
              ]}
            />
            <Section title="Vidéos">
              <RemoteList
                buildPath={(r) => `/pro/${r.id}/videos`}
                emptyText="Aucune vidéo"
                columns={[
                  { key: "title", label: "Titre", render: (v) => pick(v, "title", "name") || "—" },
                  { key: "published", label: "Publiée", render: (v) => (pick(v, "published") ? "Oui" : "Non") },
                  { key: "date", label: "Créée le", render: (v) => fmtDate(pick(v, "createdAt")) },
                ]}
              />
            </Section>
            <Section title="Exercices">
              <RemoteList
                buildPath={(r) => `/pro/${r.id}/exercises`}
                emptyText="Aucun exercice"
                columns={[
                  { key: "title", label: "Titre", render: (e) => pick(e, "title", "name") || "—" },
                  { key: "category", label: "Catégorie", render: (e) => pick(e, "category") || "—" },
                  { key: "active", label: "Actif", render: (e) => (pick(e, "active") ? "Oui" : "Non") },
                  { key: "date", label: "Créé le", render: (e) => fmtDate(pick(e, "createdAt")) },
                ]}
              />
            </Section>
          </Stack>
        </T>

        <T label="Avis" path="avis">
          <Stack spacing={3}>
            <RemoteList
              buildPath={(r) => `/reviews/users/${r.id}/received`}
              emptyText="Aucun avis"
              columns={[
                { key: "rating", label: "Note", render: (a) => <Stars value={pick(a, "rating", "note")} /> },
                { key: "comment", label: "Commentaire", render: (a) => pick(a, "comment", "text") || "—" },
                { key: "author", label: "Auteur", render: (a) => pick(a, "authorName", "author.firstName", "author") || "—" },
                { key: "date", label: "Date", render: (a) => fmtDate(pick(a, "createdAt", "date")) },
              ]}
            />
            <Section title="Matching (compatibilité)">
              <MatchingPanel />
            </Section>
          </Stack>
        </T>

        <T label="Parrainage" path="parrainage">
          <ReferralPanel />
        </T>

        <T label="Statistiques" path="stats">
          <ProStatsPanel />
        </T>

        <T label="Messages & préférences" path="messages">
          <Stack spacing={3}>
            <Section title="Conversations">
              <RemoteList
                buildPath={(r) => `/chat/all-conversation?recipientId=${r.id}&limit=100`}
                emptyText="Aucune conversation"
                columns={[
                  { key: "with", label: "Interlocuteur", render: (c) => pick(c, "patientName", "proName") || "—" },
                  { key: "content", label: "Dernier message", render: (c) => pick(c, "content") || "—" },
                  { key: "date", label: "Date", render: (c) => fmtDateTime(pick(c, "timestamp", "createdAt")) },
                  { key: "read", label: "Lu", render: (c) => (pick(c, "readByRecipient") ? "Oui" : "Non") },
                ]}
              />
            </Section>
            <CardGrid min={400}>
              <Section title="Notifications & paramètres">
                <InfoGrid
                  fields={[
                    { label: "Notifications SMS", source: "settings.receiveNotificationsBySms", type: "bool" },
                    { label: "Notifications email", source: "settings.receiveNotificationsByEmail", type: "bool" },
                    { label: "Marketing", source: "settings.marketingNotifications", type: "bool" },
                    { label: "Rapport hebdo", source: "settings.weeklyReport", type: "bool" },
                    { label: "Rappel séance (min avant)", source: "settings.consultationReminderMinutesBefore" },
                    { label: "Enregistrement vidéo autorisé", source: "settings.allowVideoRecording", type: "bool" },
                    { label: "Appels depuis la messagerie", source: "settings.allowCallsFromMessaging", type: "bool" },
                    { label: "Langue", source: "settings.language" },
                  ]}
                />
              </Section>
              <Section title="Messages par défaut">
                <InfoGrid
                  cols={1}
                  fields={[
                    { label: "Introduction", source: "settings.defaultMessageIntroduction" },
                    { label: "Préparation de séance", source: "settings.defaultMessagePreparation" },
                    { label: "Message additionnel", source: "settings.additionalMessage" },
                  ]}
                />
              </Section>
            </CardGrid>
            <Section title="Proches déclarés">
              <RemoteList
                buildPath={(r) => `/users/${r.id}`}
                extract={(json) => (Array.isArray(json?.proches) ? json.proches : [])}
                emptyText="Aucun proche déclaré"
                columns={[
                  { key: "name", label: "Nom", render: (p) => fullName(p.firstName, p.lastName, "—") },
                  { key: "birth", label: "Naissance", render: (p) => fmtDate(p.dateOfBirth) },
                  { key: "gender", label: "Genre", render: (p) => labelOf(GENDER, p.gender) },
                  { key: "email", label: "Email", render: (p) => p.email || "—" },
                  { key: "phone", label: "Téléphone", render: (p) => p.phone || "—" },
                ]}
              />
            </Section>
          </Stack>
        </T>
      </TabbedShowLayout>
    </Card>
  </Show>
);

// Adresses de cabinet : ProfessionalCardDTO.professionalAddresses (repli :
// ProfessionalDTO.professionalAddresses). Lecture seule : l'API admin ne les
// met pas à jour via PUT /users/pro/{id} (le pro les gère depuis son app).
const CabinetAddresses = () => {
  const r = useRecordContext();
  const list = (r?.professionalCard?.professionalAddresses?.length ? r.professionalCard.professionalAddresses : r?.professionalAddresses) || [];
  if (!list.length) return <MuiTypography variant="body2" color="text.secondary">Aucune adresse de cabinet renseignée</MuiTypography>;
  return (
    <Box sx={{ overflowX: "auto", border: 1, borderColor: "divider", borderRadius: 3 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Nom</TableCell>
            <TableCell>Adresse</TableCell>
            <TableCell>Téléphone</TableCell>
            <TableCell>Accès</TableCell>
            <TableCell>Infos utiles</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {list.map((a, i) => (
            <TableRow key={i} hover>
              <TableCell>{a?.adresseName || `Cabinet ${i + 1}`}</TableCell>
              <TableCell>{[a?.street, [a?.postalCode, a?.city].filter(Boolean).join(" "), a?.country].filter(Boolean).join(", ") || "—"}</TableCell>
              <TableCell>{a?.phone || "—"}</TableCell>
              <TableCell>
                {[a?.floor != null && a.floor !== "" ? `Étage ${a.floor}` : null, a?.elevator ? "Ascenseur" : null, a?.parking ? "Parking" : null, a?.accessible ? "PMR" : null]
                  .filter(Boolean)
                  .join(" · ") || "—"}
              </TableCell>
              <TableCell>{a?.usefulInformations || "—"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

const ProHeaderChips = () => {
  const r = useRecordContext();
  if (!r) return null;
  return (
    <>
      {isValidated(r) ? <Chip size="small" color="success" label="Validé" /> : <Chip size="small" color="warning" label="À valider" />}
      {isFeatured(r) && <Chip size="small" color="secondary" label="Mis en avant" />}
      {r.job && <Chip size="small" variant="outlined" label={r.job} />}
    </>
  );
};

const MatchingPanel = () => (
  <RemoteObject
    buildPath={(r) => `/pro/${r.id}/matching`}
    emptyText="Aucune donnée de matching"
    render={(m) => (
      <Box>
        <MuiTypography variant="subtitle2">
          Compatibilité : {typeof m.matchingPercent === "number" ? `${m.matchingPercent} %` : "non applicable"}
        </MuiTypography>
        <MuiTypography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Le % de compatibilité est calculé pour UN patient donné (traits choisis pendant sa conversation guidée vs
          traits renseignés par le pro). Il n'existe pas de score global d'un praticien.
        </MuiTypography>
        {(m.experienceCount ?? 0) > 0 && m.impact ? (
          <Stack spacing={0.5} sx={{ mt: 1 }}>
            <MuiTypography variant="body2">Expériences : {m.experienceCount}</MuiTypography>
            <MuiTypography variant="body2">Compréhension : {m.impact.comprehension}</MuiTypography>
            <MuiTypography variant="body2">Nouvelle piste : {m.impact.nouvellePiste}</MuiTypography>
            <MuiTypography variant="body2">Action : {m.impact.action}</MuiTypography>
          </Stack>
        ) : (
          <MuiTypography variant="body2" sx={{ mt: 1 }}>Impact ressenti : pas encore de données agrégées.</MuiTypography>
        )}
      </Box>
    )}
  />
);

const pct = (v) => (v != null ? `${Number(v).toFixed(1).replace(".", ",")} %` : "—");

const ProStatsPanel = () => (
  <Stack spacing={3}>
    <CardGrid min={300}>
      <Section title="Patients">
        <RemoteObject
          buildPath={(r) => `/stats/users/${r.id}/patients`}
          emptyText="Aucune statistique patient"
          render={(p) => (
            <InfoGrid
              fields={[
                { label: "Patients uniques", source: () => p?.totalPatients ?? 0 },
                { label: "Âge moyen", source: () => (p?.averageAge ? `${Number(p.averageAge).toFixed(1).replace(".", ",")} ans` : "—") },
                { label: "Hommes", source: () => p?.patientsByGender?.MALE ?? 0 },
                { label: "Femmes", source: () => p?.patientsByGender?.FEMALE ?? 0 },
                { label: "Minutes consultées", source: () => p?.totalMinutes ?? 0 },
              ]}
            />
          )}
        />
      </Section>
      <Section title="Semaine en cours">
        <RemoteObject
          buildPath={(r) => `/stats/users/${r.id}/week/summary`}
          emptyText="Aucune donnée hebdo"
          render={(w) => (
            <InfoGrid
              fields={[
                { label: "RDV cette semaine", source: () => w?.currentWeekAppointments ?? 0 },
                { label: "Revenu brut", source: () => fmtMoney(w?.currentWeekRevenue) },
                { label: "Net versé", source: () => fmtMoney(w?.currentWeekNetPayout) },
                { label: "Record RDV / semaine", source: () => w?.bestWeekAppointmentsEver ?? 0 },
              ]}
            />
          )}
        />
      </Section>
    </CardGrid>
    <Section title="Mois en cours vs mois précédent">
      <RemoteObject
        buildPath={(r) => `/stats/users/${r.id}/period?period=month`}
        emptyText="Aucune comparaison mensuelle"
        render={(p) => (
          <InfoGrid
            fields={[
              { label: "Période", source: () => p?.periodLabel || "—" },
              { label: "Comparaison", source: () => p?.comparisonLabel || "—" },
              { label: "RDV mois courant", source: () => p?.current?.totalAppointments ?? 0 },
              { label: "RDV mois précédent", source: () => p?.previous?.totalAppointments ?? 0 },
              { label: "Variation RDV", source: () => pct(p?.appointmentsChangePct) },
              { label: "Revenu mois courant", source: () => fmtMoney(p?.current?.totalRevenue) },
              { label: "Revenu mois précédent", source: () => fmtMoney(p?.previous?.totalRevenue) },
              { label: "Variation revenu", source: () => pct(p?.revenueChangePct) },
              { label: "Visio (mois)", source: () => p?.current?.onlineAppointments ?? 0 },
              { label: "Cabinet (mois)", source: () => p?.current?.inPersonAppointments ?? 0 },
              { label: "Durée moy. visio", source: () => (p?.current?.averageDurationMinutesOnline ? `${Number(p.current.averageDurationMinutesOnline).toFixed(0)} min` : "—") },
              { label: "Durée moy. cabinet", source: () => (p?.current?.averageDurationMinutesInPerson ? `${Number(p.current.averageDurationMinutesInPerson).toFixed(0)} min` : "—") },
            ]}
          />
        )}
      />
    </Section>
    <Section title="Année en cours, mois par mois">
      <RemoteList
        buildPath={(r) => `/stats/users/${r.id}/year/current`}
        emptyText="Aucune statistique annuelle"
        columns={[
          { key: "month", label: "Mois", render: (m) => `${m?.month || "?"}/${m?.year || "?"}` },
          { key: "appts", label: "RDV", align: "right", render: (m) => m?.totalAppointments ?? 0 },
          { key: "online", label: "Visio", align: "right", render: (m) => m?.onlineAppointments ?? 0 },
          { key: "inperson", label: "Cabinet", align: "right", render: (m) => m?.inPersonAppointments ?? 0 },
          { key: "revenue", label: "Revenu", align: "right", render: (m) => fmtMoney(m?.totalRevenue) },
          { key: "realized", label: "Encaissé", align: "right", render: (m) => fmtMoney(m?.realizedMonthRevenue) },
        ]}
      />
    </Section>
  </Stack>
);

// Écriture : PUT /users/pro/{id} (resourceMap.professionals.updatePath).
// Validation / mise en avant / suspension : boutons dédiés de la fiche.
const ProForm = () => (
  <SimpleForm>
    <TextInput source="firstName" label="Prénom" validate={required()} />
    <TextInput source="lastName" label="Nom" validate={required()} />
    <TextInput source="email" label="Email" type="email" validate={[required(), email()]} />
    <TextInput source="phoneNumber" label="Téléphone" />
    <TextInput source="job" label="Métier" />
    <SelectInput source="gender" label="Genre" choices={choicesFromEnum(GENDER)} />
    <SelectInput source="accountStatus" label="Statut du compte" choices={choicesFromEnum(ACCOUNT_STATUS)} />
    <MuiTypography variant="subtitle2" sx={{ mt: 2 }}>Adresse du profil</MuiTypography>
    <MuiTypography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
      Les adresses de cabinet (fiche professionnelle) sont gérées par le praticien depuis son application ; elles
      sont affichées en lecture seule sur sa fiche.
    </MuiTypography>
    <TextInput source="address.street" label="Rue" />
    <TextInput source="address.postalCode" label="Code postal" />
    <TextInput source="address.city" label="Ville" />
    <TextInput source="address.country" label="Pays" />
  </SimpleForm>
);

export const ProEdit = () => (
  <Edit mutationMode="pessimistic">
    <ProForm />
  </Edit>
);

export default {
  list: ProList,
  show: ProShow,
  edit: ProEdit,
};
